/**
 * Google Identity Services SDK loader.
 *
 * Single-flight script load with `loadGoogleSdk()`. Once loaded, callers
 * `initGoogleSignIn({ clientId, onCredential })` to register a callback
 * that fires with a Google ID token when the user signs in. Render the
 * branded button into a DOM node with `renderGoogleButton(node, opts?)`.
 *
 * The clientId is whatever is provisioned in Google Cloud Console for the
 * cloud deployment — the boot probe gives it (`getApiConfig()` in api/index.ts).
 */

import { localStore } from '@/lib/webStorage';
import type { GisButtonOptions, GisIdClient } from '@/types/auth';
import { GoogleUxMode } from '@/types/auth';

const SDK_URL = 'https://accounts.google.com/gsi/client';
// Set by an explicit sign-out: the login page must not put the user straight
// back in with the account they just left.
const AUTO_SIGN_IN_OFF_KEY = 'occurra.autoSignInOff';

let _loadPromise: Promise<GisIdClient> | null = null;

export function loadGoogleSdk(): Promise<GisIdClient> {
    if (_loadPromise) {
        return _loadPromise;
    }
    _loadPromise = new Promise<GisIdClient>((resolve, reject) => {
        if (typeof window === 'undefined') {
            reject(new Error('Google Sign-In requires a browser environment'));
            return;
        }
        if (window.google?.accounts?.id) {
            resolve(window.google.accounts.id);
            return;
        }
        const s = document.createElement('script');
        s.src = SDK_URL;
        s.async = true;
        s.defer = true;
        s.onload = () => {
            const gis = window.google?.accounts?.id;
            if (gis) {
                resolve(gis);
            } else {
                _loadPromise = null; //< Same retry rule as onerror below.
                reject(new Error('Google Sign-In SDK loaded but `window.google.accounts.id` is missing'));
            }
        };
        s.onerror = () => {
            // Drop the cached promise so the next call re-attempts the script load;
            // otherwise one flaky fetch leaves sign-in dead until a full reload.
            _loadPromise = null;
            s.remove();
            reject(new Error('Failed to load Google Sign-In SDK'));
        };
        document.head.appendChild(s);
    });
    return _loadPromise;
}

interface GoogleSignInOptions {
    clientId: string;
    onCredential?: (credential?: string) => void;
    uxMode?: GoogleUxMode;
    /** Where Google form-POSTs the credential in redirect mode. */
    loginUri?: string;
}

/**
 * uxMode 'redirect' + loginUri switch the button to GIS redirect mode: the
 * CURRENT window navigates to Google and Google form-POSTs the credential to
 * loginUri (must be an authorized redirect URI of the OAuth client). Used by
 * the single-popup local-login flow (VMS-264) — no nested Google popup.
 * Default (popup mode) delivers the credential to onCredential instead.
 */
export async function initGoogleSignIn(
    { clientId, onCredential, uxMode, loginUri }: GoogleSignInOptions,
): Promise<GisIdClient> {
    if (!clientId) {
        throw new Error('initGoogleSignIn requires a clientId');
    }
    const gis = await loadGoogleSdk();
    gis.initialize({
        client_id: clientId,
        callback: ({ credential }) => onCredential?.(credential),
        // Lets One Tap sign a returning user back in without a click once their
        // session has run out.
        auto_select: true,
        cancel_on_tap_outside: true,
        ...(uxMode === GoogleUxMode.Redirect
            ? { ux_mode: GoogleUxMode.Redirect, login_uri: loginUri }
            : {}),
    });
    return gis;
}

export function renderGoogleButton(node: HTMLElement, opts: GisButtonOptions = {}): void {
    const gis = window.google?.accounts?.id;
    if (!gis) {
        throw new Error('Call initGoogleSignIn() before renderGoogleButton()');
    }
    gis.renderButton(node, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        ...opts,
    });
}

/**
 * Shows Google One Tap, which signs the user straight back in when Google
 * holds exactly one account that has used this app before. Skipped after an
 * explicit sign-out until the next sign-in.
 */
export function promptGoogleSignIn(): void {
    if (localStore.readText(AUTO_SIGN_IN_OFF_KEY) !== null) {
        return;
    }
    window.google?.accounts?.id?.prompt();
}

/** Closes One Tap when the sign-in screen goes away under it. */
export function cancelGooglePrompt(): void {
    window.google?.accounts?.id?.cancel();
}

/** Lifts an explicit sign-out's hold on One Tap: the user is signed in again. */
export function allowGoogleAutoSignIn(): void {
    localStore.remove(AUTO_SIGN_IN_OFF_KEY);
}

/**
 * Revokes the current Google session (best-effort — failures are silent
 * because the local app-side logout has already happened).
 */
export function signOutGoogle(email?: string): void {
    // Held by the app itself: the SDK is not loaded on most pages a user
    // signs out from, so its own disableAutoSelect below rarely runs.
    localStore.writeText(AUTO_SIGN_IN_OFF_KEY, '1');
    const gis = window.google?.accounts?.id;
    if (!gis) {
        return;
    }
    try {
        gis.disableAutoSelect();
        if (email) {
            gis.revoke(email, () => {});
        }
    } catch { /* ignore */ }
}
